/**
 * @fileoverview Admin dashboard for managing reported content and user contributions.
 * 
 * This component provides a comprehensive interface for admins to review
 * and manage reported content, user contributions, and system statistics.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminAuthServiceGmail from '../../services/adminAuthServiceGmail';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle, 
  Eye, 
  Clock, 
  User, 
  MessageSquare,
  FileText,
  Settings,
  Flag,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { useTheme } from '../../lib/ThemeContext';
import { useQuestionReports, ReportData, ReportStatus, questionReportService } from '../../services/questionReportService';
import { contactFormService, ContactFormData, ContactStatus } from '../../services/contactFormService';
import { adminService } from '../../services/adminService';
import { getAllAdminConfigs, getCurrentAdminConfig, getUserRole } from '../../config/adminConfig';
import { getEnvironment } from '../../config/environment';

// Contact Submissions Tab Component
const ContactSubmissionsTab: React.FC = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const [submissions, setSubmissions] = useState<ContactFormData[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactFormData | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    contactType: '',
    search: ''
  });
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const statusOptions = contactFormService.getStatusOptions();

  useEffect(() => {
    loadSubmissions();
    loadStats();
  }, []);

  const loadSubmissions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await contactFormService.getContactSubmissions();
      if (result.error) {
        setError(result.error);
      } else {
        setSubmissions(result.submissions);
      }
    } catch (err) {
      setError('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await contactFormService.getContactStats();
      if (!result.error) {
        setStats(result);
      }
    } catch (err) {
      console.error('Failed to load contact stats:', err);
    }
  };

  const handleStatusUpdate = async (contactId: string, newStatus: ContactStatus, adminNotes?: string, adminResponse?: string) => {
    setUpdatingStatus(contactId);
    
    try {
      const result = await contactFormService.updateContactStatus(contactId, newStatus, adminNotes, adminResponse);
      if (result.success) {
        // Update the submission in the local state
        setSubmissions(prev => prev.map(submission => 
          submission.contactId === contactId 
            ? { ...submission, status: newStatus, adminNotes, adminResponse }
            : submission
        ));
        
        // Reload stats
        loadStats();
        
        // Close the detail modal if open
        if (selectedSubmission?.contactId === contactId) {
          setSelectedSubmission(null);
        }
      } else {
        alert(`Failed to update status: ${result.error}`);
      }
    } catch (err) {
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (contactId: string) => {
    if (!window.confirm('Are you sure you want to delete this contact submission? This action cannot be undone.')) {
      return;
    }
    
    console.log('🗑️ Attempting to delete contact submission:', contactId);
    setDeletingId(contactId);
    
    try {
      const result = await contactFormService.deleteContactSubmission(contactId);
      console.log('🗑️ Delete result:', result);
      
      if (result.success) {
        // Remove the submission from the local state
        setSubmissions(prev => {
          const filtered = prev.filter(submission => submission.contactId !== contactId);
          console.log('🗑️ Updated submissions count:', filtered.length);
          return filtered;
        });
        
        // Reload stats
        loadStats();
        
        // Close the detail modal if open
        if (selectedSubmission?.contactId === contactId) {
          setSelectedSubmission(null);
        }
      } else {
        alert(`Failed to delete submission: ${result.error}`);
      }
    } catch (err) {
      console.error('🗑️ Error deleting submission:', err);
      alert('Failed to delete submission');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredSubmissions = submissions.filter(submission => {
    if (filters.status && submission.status !== filters.status) return false;
    if (filters.contactType && submission.contactType !== filters.contactType) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        submission.name.toLowerCase().includes(searchLower) ||
        submission.email.toLowerCase().includes(searchLower) ||
        submission.subject.toLowerCase().includes(searchLower) ||
        submission.message.toLowerCase().includes(searchLower) ||
        (submission.adminNotes && submission.adminNotes.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  const getStatusColor = (status: ContactStatus) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      case 'Responded': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      case 'Spam': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: ContactStatus) => {
    switch (status) {
      case 'New': return <Clock size={14} />;
      case 'In Progress': return <Settings size={14} />;
      case 'Responded': return <CheckCircle size={14} />;
      case 'Closed': return <FileText size={14} />;
      case 'Spam': return <Eye size={14} />;
      default: return <Clock size={14} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading contact submissions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">New</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.new || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Settings className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">In Progress</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.inProgress || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Responded</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.responded || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <FileText className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Contact Submissions ({filteredSubmissions.length})
            </h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <Filter size={16} className="mr-1" />
              Filters
              {showFilters ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
            </button>
          </div>
          
          {showFilters && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Type
                </label>
                <select
                  value={filters.contactType}
                  onChange={(e) => setFilters(prev => ({ ...prev, contactType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">All Types</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Bug Report">Bug Report</option>
                  <option value="Feature Request">Feature Request</option>
                  <option value="Feedback">Feedback</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search submissions..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submissions List */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
          
          {filteredSubmissions.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No submissions found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filters.status || filters.contactType || filters.search 
                  ? 'Try adjusting your filters' 
                  : 'No contact submissions yet'}
              </p>
            </div>
          ) : (
            filteredSubmissions.map((submission) => (
              <div key={submission.contactId} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                        {getStatusIcon(submission.status)}
                        <span className="ml-1">{submission.status}</span>
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(submission.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {submission.subject}
                    </h4>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      <strong>From:</strong> {submission.name} ({submission.email})
                    </p>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                      <strong>Type:</strong> {submission.contactType}
                    </p>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                      {submission.message}
                    </p>
                  </div>
                  
                                     <div className="flex flex-col space-y-2 ml-4">
                     <button
                       onClick={() => setSelectedSubmission(submission)}
                       className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
                     >
                       View Details
                     </button>
                     
                     {submission.status === 'New' && (
                       <button
                         onClick={() => handleStatusUpdate(submission.contactId, 'In Progress')}
                         disabled={updatingStatus === submission.contactId}
                         className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                       >
                         {updatingStatus === submission.contactId ? 'Updating...' : 'Mark In Progress'}
                       </button>
                     )}
                     
                     {submission.status === 'In Progress' && (
                       <button
                         onClick={() => handleStatusUpdate(submission.contactId, 'Responded')}
                         disabled={updatingStatus === submission.contactId}
                         className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                       >
                         {updatingStatus === submission.contactId ? 'Updating...' : 'Mark Responded'}
                       </button>
                     )}
                     
                     <button
                       onClick={() => handleDelete(submission.contactId)}
                       disabled={deletingId === submission.contactId}
                       className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                     >
                       {deletingId === submission.contactId ? 'Deleting...' : 'Delete'}
                     </button>
                   </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Contact Submission Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Contact Submission Details
              </h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contact Type</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.contactType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusColor(selectedSubmission.status)}`}>
                    {getStatusIcon(selectedSubmission.status)}
                    <span className="ml-1">{selectedSubmission.status}</span>
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subject</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.subject}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedSubmission.message}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Browser Info</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.browserInfo}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">App Version</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.appVersion}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Submitted</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {new Date(selectedSubmission.timestamp).toLocaleString()}
                </p>
              </div>

              {selectedSubmission.adminNotes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Admin Notes</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.adminNotes}</p>
                </div>
              )}

              {selectedSubmission.adminResponse && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Admin Response</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedSubmission.adminResponse}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// User Contributions Tab Component
const UserContributionsTab: React.FC = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [contributions, setContributions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContribution, setSelectedContribution] = useState<any | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    gameType: '',
    search: ''
  });
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadContributions();
  }, []);

  const loadContributions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await adminService.getUserContributions();
      setContributions(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load user contributions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (contributionId: string, newStatus: 'approved' | 'rejected', reason?: string) => {
    setUpdatingStatus(contributionId);
    
    try {
      await adminService.reviewContribution(contributionId, newStatus, reason);
      // Update the contribution in the local state
      setContributions(prev => prev.map(contribution => 
        contribution.id === contributionId 
          ? { ...contribution, status: newStatus }
          : contribution
      ));
      
      // Close the detail modal if open
      if (selectedContribution?.id === contributionId) {
        setSelectedContribution(null);
      }
    } catch (err: any) {
      alert(`Failed to update status: ${err.message}`);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (contributionId: string) => {
    if (!window.confirm('Are you sure you want to delete this user contribution? This action cannot be undone.')) {
      return;
    }
    
    console.log('🗑️ Attempting to delete user contribution:', contributionId);
    setDeletingId(contributionId);
    
    try {
      const result = await adminService.deleteContribution(contributionId);
      console.log('🗑️ Delete result:', result);
      
      if (result.success) {
        // Remove the contribution from the local state
        setContributions(prev => {
          const filtered = prev.filter(contribution => contribution.id !== contributionId);
          console.log('🗑️ Updated contributions count:', filtered.length);
          return filtered;
        });
        
        // Close the detail modal if open
        if (selectedContribution?.id === contributionId) {
          setSelectedContribution(null);
        }
      } else {
        alert(`Failed to delete contribution: ${result.error}`);
      }
    } catch (err: any) {
      console.error('🗑️ Error deleting contribution:', err);
      alert(`Failed to delete contribution: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredContributions = contributions.filter(contribution => {
    if (filters.status && contribution.status !== filters.status) return false;
    if (filters.gameType && contribution.gameType !== filters.gameType) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        contribution.content.toLowerCase().includes(searchLower) ||
        (contribution.userEmail && contribution.userEmail.toLowerCase().includes(searchLower)) ||
        (contribution.userName && contribution.userName.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock size={14} />;
      case 'approved': return <CheckCircle size={14} />;
      case 'rejected': return <X size={14} />;
      default: return <Clock size={14} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading user contributions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">User Contributions</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Review and manage user-submitted questions and content
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              <Filter size={16} className="mr-2" />
              Filters
            </button>

          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{contributions.filter(c => c.status === 'pending').length}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{contributions.filter(c => c.status === 'approved').length}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Approved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{contributions.filter(c => c.status === 'rejected').length}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Rejected</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Game Type</label>
              <select
                value={filters.gameType}
                onChange={(e) => setFilters(prev => ({ ...prev, gameType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All Games</option>
                <option value="trivia">Trivia</option>
                <option value="would-you-rather">Would You Rather</option>
                <option value="truth-dare">Truth or Dare</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search content, email, or name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Contributions List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            User Contributions ({filteredContributions.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredContributions.length === 0 ? (
            <div className="p-8 text-center">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No contributions found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filters.status || filters.gameType || filters.search 
                  ? 'Try adjusting your filters' 
                  : 'No user contributions yet'}
              </p>
            </div>
          ) : (
            filteredContributions.map((contribution) => (
              <div key={contribution.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {contribution.gameType}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contribution.status)}`}>
                        {getStatusIcon(contribution.status)}
                        <span className="ml-1">{contribution.status}</span>
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(contribution.createdAt?.toDate?.() || contribution.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {contribution.content}
                    </h4>
                    
                    {contribution.answer && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                        <strong>Answer:</strong> {contribution.answer}
                      </p>
                    )}
                    
                    {contribution.options && contribution.options.length > 0 && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                        <strong>Options:</strong> {contribution.options.join(', ')}
                      </p>
                    )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {contribution.userEmail && `By: ${contribution.userEmail}`}
                      {contribution.userName && ` (${contribution.userName})`}
                    </p>
                  </div>
                  
                                     <div className="flex flex-col space-y-2 ml-4">
                     <button
                       onClick={() => setSelectedContribution(contribution)}
                       className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
                     >
                       View Details
                     </button>
                     
                     {contribution.status === 'pending' && (
                       <>
                         <button
                           onClick={() => handleStatusUpdate(contribution.id, 'approved')}
                           disabled={updatingStatus === contribution.id}
                           className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                         >
                           {updatingStatus === contribution.id ? 'Updating...' : 'Approve'}
                         </button>
                         
                         <button
                           onClick={() => handleStatusUpdate(contribution.id, 'rejected')}
                           disabled={updatingStatus === contribution.id}
                           className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                         >
                           {updatingStatus === contribution.id ? 'Updating...' : 'Reject'}
                         </button>
                       </>
                     )}
                     
                     <button
                       onClick={() => handleDelete(contribution.id)}
                       disabled={deletingId === contribution.id}
                       className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                     >
                       {deletingId === contribution.id ? 'Deleting...' : 'Delete'}
                     </button>
                   </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Contribution Detail Modal */}
      {selectedContribution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Contribution Details
              </h2>
              <button
                onClick={() => setSelectedContribution(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Game Type</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.gameType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.status}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Difficulty</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.difficulty}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.category}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.content}</p>
              </div>

              {selectedContribution.answer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Answer</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.answer}</p>
                </div>
              )}

              {selectedContribution.options && selectedContribution.options.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Options</label>
                  <ul className="mt-1 text-sm text-gray-900 dark:text-white list-disc list-inside">
                    {selectedContribution.options.map((option: string, index: number) => (
                      <li key={index}>{option}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User Email</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.userEmail || 'Anonymous'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User Name</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.userName || 'Anonymous'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Submitted</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {new Date(selectedContribution.createdAt?.toDate?.() || selectedContribution.createdAt).toLocaleString()}
                </p>
              </div>

              {selectedContribution.adminNotes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Admin Notes</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedContribution.adminNotes}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setSelectedContribution(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Question Reports Tab Component
const QuestionReportsTab: React.FC = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [reports, setReports] = useState<ReportData[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { updateReportStatus } = useQuestionReports();

  useEffect(() => {
    loadReports();
    loadStats();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await questionReportService.getReports();
      if (result.error) {
        setError(result.error);
      } else {
        setReports(result.reports);
      }
    } catch (err) {
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await questionReportService.getReportStats();
      if (!result.error) {
        setStats(result);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const getStatusColor = (status: ReportStatus) => {
    switch (status) {
      case 'Reported': return 'bg-red-100 text-red-800';
      case 'In Review': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: ReportStatus) => {
    switch (status) {
      case 'Reported': return <Clock size={14} />;
      case 'In Review': return <Eye size={14} />;
      case 'Completed': return <CheckCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const handleDelete = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this question report? This action cannot be undone.')) {
      return;
    }
    
    console.log('🗑️ Attempting to delete question report:', reportId);
    setDeletingId(reportId);
    
    try {
      const result = await questionReportService.deleteReport(reportId);
      console.log('🗑️ Delete result:', result);
      
      if (result.success) {
        // Remove the report from the local state
        setReports(prev => {
          const filtered = prev.filter(report => report.id !== reportId);
          console.log('🗑️ Updated reports count:', filtered.length);
          return filtered;
        });
        
        // Reload stats
        loadStats();
        
        // Close the detail modal if open
        if (selectedReport?.id === reportId) {
          setSelectedReport(null);
        }
      } else {
        alert(`Failed to delete report: ${result.error}`);
      }
    } catch (err) {
      console.error('🗑️ Error deleting report:', err);
      alert('Failed to delete report');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading question reports...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <Flag className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Reports</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <Clock className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Reported</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.reported || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Eye className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">In Review</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.inReview || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.completed || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reports List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Question Reports ({reports.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
          
          {reports.length === 0 ? (
            <div className="p-8 text-center">
              <Flag className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No reports found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                No question reports have been submitted yet.
              </p>
            </div>
          ) : (
            reports.map((report) => (
              <div key={report.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {report.gameType}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(report.timestamp).toLocaleString()}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                        {getStatusIcon(report.status)}
                        <span className="ml-1">{report.status}</span>
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      Question: {report.questionText}
                    </h4>
                    
                                         <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                       <strong>Reason:</strong> {report.reportReason}
                     </p>
                     
                     {report.improvementSuggestion && (
                       <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                         <strong>Suggestion:</strong> {report.improvementSuggestion}
                       </p>
                     )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Reported by: {report.reporterEmail} ID: {report.id}
                    </p>
                  </div>
                  
                                     <div className="flex flex-col space-y-2 ml-4">
                     
                     
                     <button
                       onClick={() => setSelectedReport(report)}
                       className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium"
                     >
                       View Details
                     </button>
                     
                     {report.status === 'Reported' && report.id && (
                       <button
                         onClick={() => updateReportStatus(report.id!, 'In Review')}
                         className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-xs font-medium"
                       >
                         Mark In Review
                       </button>
                     )}
                     
                     <button
                       onClick={() => handleDelete(report.id!)}
                       disabled={deletingId === report.id}
                       className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                     >
                       {deletingId === report.id ? 'Deleting...' : 'Delete'}
                     </button>
                   </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Question Report Details
              </h2>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Game Type</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedReport.gameType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusColor(selectedReport.status)}`}>
                    {getStatusIcon(selectedReport.status)}
                    <span className="ml-1">{selectedReport.status}</span>
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Report ID</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedReport.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reported</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {new Date(selectedReport.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Question Text</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedReport.questionText}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Report Reason</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedReport.reportReason}</p>
              </div>

              {selectedReport.improvementSuggestion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Improvement Suggestion</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedReport.improvementSuggestion}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reporter Email</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedReport.reporterEmail || 'Anonymous'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Question Type</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedReport.questionType || 'Not specified'}</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setSelectedReport(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Management Tab Component
const AdminManagementTab: React.FC = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [currentUser] = useState(adminAuthServiceGmail.getCurrentUser());
  const [currentConfig, setCurrentConfig] = useState(getCurrentAdminConfig());
  const [allConfigs, setAllConfigs] = useState(getAllAdminConfigs());

  const currentUserRole = currentUser ? getUserRole(currentUser.email) : 'none';
  const currentEnvironment = getEnvironment();

  return (
    <div className="space-y-6">
      {/* Current Environment Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Current Environment: {currentEnvironment}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Your Email</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-white">{currentUser?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Your Role</label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
              currentUserRole === 'primary' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
              currentUserRole === 'reviewer' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
              currentUserRole === 'moderator' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
              'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
            }`}>
              {currentUserRole.charAt(0).toUpperCase() + currentUserRole.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Current Environment Admins */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {currentEnvironment.charAt(0).toUpperCase() + currentEnvironment.slice(1)} Environment Admins
        </h3>
        
        <div className="space-y-4">
          {/* Primary Admin */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Primary Admin</h4>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                Primary
              </span>
              <span className="text-sm text-gray-900 dark:text-white">{currentConfig.primaryAdmin}</span>
            </div>
          </div>

          {/* Reviewers */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reviewers</h4>
            {currentConfig.reviewers.length > 0 ? (
              <div className="space-y-1">
                {currentConfig.reviewers.map((email, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      Reviewer
                    </span>
                    <span className="text-sm text-gray-900 dark:text-white">{email}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No reviewers configured</p>
            )}
          </div>

          {/* Moderators */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Moderators</h4>
            {currentConfig.moderators.length > 0 ? (
              <div className="space-y-1">
                {currentConfig.moderators.map((email, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Moderator
                    </span>
                    <span className="text-sm text-gray-900 dark:text-white">{email}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No moderators configured</p>
            )}
          </div>
        </div>
      </div>

      {/* All Environments Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          All Environments Overview
        </h3>
        
        <div className="space-y-4">
          {Object.entries(allConfigs).map(([env, config]) => (
            <div key={env} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                {env} Environment
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="font-medium">Primary:</span> {config.primaryAdmin}
                </div>
                <div>
                  <span className="font-medium">Reviewers:</span> {config.reviewers.length}
                </div>
                <div>
                  <span className="font-medium">Moderators:</span> {config.moderators.length}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
          How to Add/Remove Admins
        </h4>
        <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <p>• To add new admin emails, edit the <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">src/config/adminConfig.ts</code> file</p>
          <p>• Update the <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">ADMIN_CONFIGS</code> object with new email addresses</p>
          <p>• After adding emails, update Firestore rules in <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">firestore.rules</code></p>
          <p>• Deploy the updated rules using <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">npm run deploy:dev:firestore</code> or <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">npm run deploy:prod:firestore</code></p>
        </div>
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'question-reports' | 'contact-submissions' | 'user-contributions' | 'admin-management'>('question-reports');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = adminAuthServiceGmail.getCurrentUser();
        const isAdmin = adminAuthServiceGmail.isAdmin();
        
        if (!currentUser || !isAdmin) {
          navigate('/appadmin/login');
          return;
        }
        setLoading(false);
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/appadmin/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await adminAuthServiceGmail.logout();
      navigate('/appadmin/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Welcome, {adminAuthServiceGmail.getCurrentUser()?.email}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <nav className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab('question-reports')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center ${
                  activeTab === 'question-reports'
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Flag size={16} className="mr-2" />
                Question Reports
              </button>
              <button
                onClick={() => setActiveTab('contact-submissions')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center ${
                  activeTab === 'contact-submissions'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <MessageSquare size={16} className="mr-2" />
                Contact Submissions
              </button>
              <button
                onClick={() => setActiveTab('user-contributions')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center ${
                  activeTab === 'user-contributions'
                    ? 'border-green-500 text-green-600 dark:text-green-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <User size={16} className="mr-2" />
                User Contributions
              </button>
              <button
                onClick={() => setActiveTab('admin-management')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200 flex items-center ${
                  activeTab === 'admin-management'
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Settings size={16} className="mr-2" />
                Admin Management
              </button>
            </div>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'question-reports' ? (
          <QuestionReportsTab />
        ) : activeTab === 'contact-submissions' ? (
          <ContactSubmissionsTab />
        ) : activeTab === 'user-contributions' ? (
          <UserContributionsTab />
        ) : activeTab === 'admin-management' ? (
          <AdminManagementTab />
        ) : null}
      </div>
    </div>
  );
};

export default AdminDashboard; 